import { hashPersonalMessage } from '@exodus/ethereumjs/util'
import {
  personalSign,
  signTypedData,
  SignTypedDataVersion,
  TypedDataUtils,
  typedSignatureHash,
} from '@metamask/eth-sig-util'
import assert from 'minimalistic-assert'

import { normalizeRecoveryParam } from './utils/ecdsa.js'

function hex0xStringToBuffer(hex) {
  // Remove the 0x
  const hexWithout0x = hex.startsWith('0x') ? hex.slice(2) : hex
  // Pad the value until even
  const hexWithLeadingZeros = hexWithout0x.length % 2 === 0 ? hexWithout0x : '0' + hexWithout0x
  // Finally return the buffer
  return Buffer.from(hexWithLeadingZeros, 'hex')
}

/**
 * Converts a buffer to the 0xhex encoded value
 * @param {Buffer} buf the buffer to convert to 0xHEXSTRING
 * @returns the hex-encoded value prepended with 0x
 */
function bufferToHex0xString(buf) {
  if (!Buffer.isBuffer(buf)) {
    throw new TypeError('expected a buffer')
  }

  const hex = buf.toString('hex')

  if (typeof hex !== 'string') {
    throw new TypeError('expected hex string')
  }

  return '0x' + hex
}

export const signMessage = async ({ privateKey, message }) => {
  const { rawMessage, EIP712Message } = message

  // Exclusive OR (XOR)
  assert(!!rawMessage !== !!EIP712Message, 'Need either rawMessage or EIP712Message')

  if (rawMessage) {
    return hex0xStringToBuffer(personalSign({ privateKey, data: bufferToHex0xString(rawMessage) }))
  }

  if (EIP712Message) {
    const version = Array.isArray(EIP712Message) ? SignTypedDataVersion.V1 : SignTypedDataVersion.V4

    return hex0xStringToBuffer(signTypedData({ privateKey, data: EIP712Message, version }))
  }
}

/**
 * @param {object} params
 * @param {{rawMessage: Buffer, EIP712Message: any}} params.message
 * @param {object} signer
 * @returns {Promise<string>}
 */
export async function signMessageWithSigner({ message, signer }) {
  const { rawMessage, EIP712Message } = message
  assert(!!rawMessage !== !!EIP712Message, 'Need either rawMessage or EIP712Message')

  const msgHash = EIP712Message
    ? getEIP712MessageHash(EIP712Message)
    : hashPersonalMessage(rawMessage)

  const { signature, recovery } = await signer.sign({
    data: msgHash,
    enc: 'sig,rec',
    signatureType: 'ecdsa',
  })

  const recoveryNormalized = normalizeRecoveryParam(recovery, { includeBase: true })
  return Buffer.concat([signature, new Uint8Array([recoveryNormalized])])
}

function getEIP712MessageHash(message) {
  const version = Array.isArray(message) ? SignTypedDataVersion.V1 : SignTypedDataVersion.V4
  return version === SignTypedDataVersion.V1
    ? hex0xStringToBuffer(typedSignatureHash(message))
    : TypedDataUtils.eip712Hash(message, version)
}
